import { useCallback } from "react";
import { API_BASE_URL } from "@/constants/app.constant";
import { useFetch } from "@/hooks/useFetch";
import useAuthService from "@/services/auth.service";
import { APIResponse, PaginationInfo } from "@/interfaces/common.interface";
import { ConversationType } from "@/constants/chat.constant";
import { ChatMessageList, Feedback } from "@/interfaces/chat.interface";

export interface FeedbackResponse extends APIResponse<ChatMessageList[]> {
  pagination: PaginationInfo;
}

/**
 * useFeedbackManagerService hook provides feedback management API service functions.
 * Handles fetching all feedback messages for review.
 * 
 * @hook
 * @returns {Object} Feedback manager service methods
 * @returns {Function} returns.getFeedbackMessages - Get all feedback messages with pagination
 * 
 * @example
 * ```tsx
 * const { getFeedbackMessages } = useFeedbackManagerService();
 * 
 * const messages = await getFeedbackMessages(1, 10, ConversationType.CHOP_GPT);
 * ```
 */
const useFeedbackManagerService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();
  const FEEDBACK_API_BASE_URL = `${API_BASE_URL}/admin/feedback/messages`;

  /**
   * Fetches all feedback messages with pagination and optional conversation type filter.
   * 
   * @param {number} [page=1] - Page number
   * @param {number} [size=10] - Page size
   * @param {ConversationType | null} [conversationType] - Optional conversation type filter
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<FeedbackResponse>} Paginated feedback messages response
   * @throws {Error} If token is invalid
   */
  const getFeedbackMessages = useCallback(
    async (
      page: number = 1,
      size: number = 10,
      conversationType?: ConversationType | null,
      signal?: AbortSignal
    ): Promise<FeedbackResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      let url = `${FEEDBACK_API_BASE_URL}?page=${page}&size=${size}`;
      if (conversationType) {
        url += `&conversation_type=${conversationType}`;
      }

      const response = await fetchService.get<FeedbackResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  const exportFeedbackReport = useCallback(
    async (conversationType: ConversationType): Promise<Blob> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const url = `${API_BASE_URL}/admin/feedback/export?conversation_type=${conversationType}`;

      const response = await fetchService.get<Blob>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getFeedbackMessages,
    exportFeedbackReport,
  };
};

export default useFeedbackManagerService;
